import {Component, computed, ElementRef, HostListener, OnDestroy, OnInit, signal, TemplateRef, ViewChild, ViewContainerRef} from '@angular/core';
import {UserService} from '../services/user/user.service';
import {User} from '../classes/user.class';
import {RegistrationService} from '../services/registration.service';
import {AzureProjectPR, AzurePullRequest, AzureService, AzureTask, QueryLinks} from '../services/azure.service';
import {CdkDrag, CdkDragDrop, CdkDropList, CdkDropListGroup, moveItemInArray} from '@angular/cdk/drag-drop';
import {ConnectedPosition, FlexibleConnectedPositionStrategy, Overlay, OverlayRef} from '@angular/cdk/overlay';
import {PortalModule, TemplatePortal} from '@angular/cdk/portal';
import {ProjectShortNamePipe} from '../pipes/project-short-name.pipe';
import {CodaltComponent} from '../codalt.component';
import {formatDate, NgTemplateOutlet} from '@angular/common';
import {ChangedByNameSplittingPipe} from '../pipes/changed-by-name-splitting.pipe';
import {CodaltDatePipe} from '../pipes/codalt-date.pipe';
import {Platform} from '../classes/platform';
import {PlatformService} from '../services/platform.service';
import {UrlPrefixStrippingPipe} from '../pipes/url-prefix-stripping.pipe';
import {UrlCombiningPipe} from '../pipes/url-combining.pipe';
import {LeaveService} from '../services/leave.service';
import {forkJoin, from, of, Subscription} from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {MatTooltip} from '@angular/material/tooltip';
import {MatFormField, MatPrefix} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatCheckbox} from '@angular/material/checkbox';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {LeaveType} from '../classes/leave';
import {AuthorisationService} from '../services/auth/authorisation.service';
import {Roles} from '../services/auth/roles';
import {AbsentGradientPipe} from '../pipes/absent-gradient.pipe';
import {AzureTaskSortingService} from '../services/azure-task-sorting.service';
import {AzureTaskSorting} from '../classes/azure-task-sorting';
import {AvatarComponent, EmptyStateComponent, FilterChipsComponent, FilterOption, formatDuration, KbdComponent, PageComponent, PageHeaderComponent, SectionComponent, TabGroupComponent, TabOption, voteFromAzure} from '../shared/ui';
import {TaskPathPart, TaskPriority, TaskRowComponent, taskStatusDot, taskStatusTone, TaskStatusTone} from '../shared/ui/task-row/task-row.component';
import {PrCardComponent, PrReviewer} from '../shared/ui/pr-card/pr-card.component';
import {HoursDay, HoursWeekComponent} from '../shared/ui/hours-week/hours-week.component';
import {CustomerColorPipe} from '../shared/customer-color.pipe';

export type DailyView = 'mine' | 'colleagues' | 'kanban';

/** Status chips in Mijn daily and Collega's, in the order of the design; other states are appended. */
const DAILY_STATES = ['Te testen', 'Active', 'Resolved', 'New', 'Backlog', 'On hold'];
/** Kanban columns (no Backlog / On hold). */
const KANBAN_STATES = ['New', 'Active', 'Te testen', 'Resolved'];
/** Work that isn't planned yet (Backlog, Indicatie goedgekeurd, ...) doesn't count in the hours next to "Open taken". */
function countsForHours(task: AzureTask): boolean {
    const state = task.state?.toLowerCase() ?? '';
    return state !== 'backlog' && !state.startsWith('indicatie');
}

const PRIORITY_LABELS: Record<TaskPriority, string> = {critical: 'Prioriteit kritiek', high: 'Prioriteit hoog', normal: 'Prioriteit normaal', low: 'Prioriteit laag'};

interface TaskView {
    href: string;
    parentHref: string | null;
    client: string;
    path: TaskPathPart[];
    companyId: number | null;
    priority: TaskPriority;
    priorityLabel: string;
    statusTone: TaskStatusTone;
}

interface PrView {
    pr: AzurePullRequest;
    project: string;
    href: string;
    when: string;
    reviewers: PrReviewer[];
}

interface HoursWeekView {
    title: string;
    range: string;
    totalMinutes: number;
    /** Contract hours of the week from the assignment periods. */
    targetMinutes: number;
    days: HoursDay[];
}

interface CustomContext {
    azure_task: AzureTask,
    platform: null | Platform[],
    relatedPRs?: { project: string; pr: AzurePullRequest }[]
}

@Component({
    selector: 'app-daily',
    templateUrl: './daily.component.html',
    styleUrls: ['./daily.component.scss'],
    imports: [
        CustomerColorPipe,
        NgTemplateOutlet,
        CdkDropList,
        CdkDropListGroup,
        CdkDrag,
        ChangedByNameSplittingPipe,
        CodaltDatePipe,
        PortalModule,
        UrlPrefixStrippingPipe,
        UrlCombiningPipe,
        MatTooltip,
        MatFormField,
        MatPrefix,
        MatInput,
        MatCheckbox,
        RouterLink,
        PageComponent,
        PageHeaderComponent,
        TabGroupComponent,
        FilterChipsComponent,
        SectionComponent,
        AvatarComponent,
        KbdComponent,
        EmptyStateComponent,
        TaskRowComponent,
        PrCardComponent,
        HoursWeekComponent,
    ]
})
export class DailyComponent extends CodaltComponent implements OnInit, OnDestroy {
    @ViewChild('taskContextMenu') taskContextMenuTpl: TemplateRef<unknown>;
    @ViewChild('taskSearch') taskSearch: ElementRef;
    readonly contextMenuTask = signal<null | CustomContext>(null);
    readonly weekList = signal<string[]>([]);
    readonly openPRs = signal<AzureProjectPR[]>([]);

    readonly taskSearchTerm = signal<string>('');
    readonly users = signal<User[]>([]);
    readonly view = signal<DailyView>('mine');
    readonly showColleagues = computed(() => this.view() === 'colleagues');
    readonly views: TabOption<DailyView>[] = [
        {value: 'mine', label: 'Mijn daily'},
        {value: 'colleagues', label: 'Collega\'s'},
        {value: 'kanban', label: 'Kanban'},
    ];
    /** Status chip filter: 'alle' or a work item state. */
    readonly statusFilter = signal<string>('alle');
    /** Kanban employee filter; empty = Iedereen. */
    readonly kanbanUserIds = signal<number[]>([]);
    // TODO(design): README "Backend nodig" — there is no API call to change the state of a work item, so dragging
    //  kanban cards between the columns is disabled (nothing would be saved). Enable this and handle
    //  (cdkDropListDropped) on the columns once the backend supports it.
    readonly kanbanStatusChangeAvailable = false;
    readonly showChanged = signal<boolean>(false);
    readonly lastSelectedTask = signal<number>(null);
    readonly userPRs = signal<{ [userId: number]: AzureProjectPR[] }>({});
    readonly userTasks = signal<{ [userId: number]: AzureTask[] }>({});
    readonly platforms = signal<Platform[]>(null)
    readonly userAbsentDates = signal<{ [userId: number]: Map<string, { color: string | null; startMinutes: number; endMinutes: number; externalName?: string; begintime?: string; endtime?: string }> }>({})
    readonly queryLinks = signal<QueryLinks[]>([]);
    readonly leaveTypes = signal<LeaveType[]>(null);
    readonly selectedUserId = signal<number>(null);

    readonly activeUserId = computed(() => this.selectedUserId() ?? this.LocalStorage.user?.id);

    readonly visibleUsers = computed(() =>
        this.showColleagues() ? this.users() : this.users().filter(user => user.id === this.activeUserId())
    );

    readonly seenUserIds = signal<number[]>([]);

    private readonly sortedUsers = computed(() => [...this.users()].sort((a, b) => a.name.localeCompare(b.name)));

    readonly markedUserIds = computed(() => {
        const absentTodayLabels = this.absentTodayLabels();
        const freeToday = Object.keys(absentTodayLabels)
            .map(Number)
            .filter(userId => ['vrij', 'ziek'].includes(absentTodayLabels[userId].name?.toLowerCase()) && !(absentTodayLabels[userId].time?.length > 0));
        return [...new Set([...this.seenUserIds(), ...freeToday])];
    });

    readonly unseenUsers = computed(() => this.sortedUsers().filter(user => !this.markedUserIds().includes(user.id)));

    readonly seenUsers = computed(() => this.sortedUsers().filter(user => this.markedUserIds().includes(user.id)));

    readonly userSwitchList = computed(() => [...this.unseenUsers(), ...this.seenUsers()]);

    readonly visibleTasks = computed(() => this.view() === 'kanban'
        ? this.kanbanColumns().flatMap(column => column.items.map(item => item.task))
        : this.visibleUsers()
            .filter(user => this.userHasResults(user.id))
            .flatMap(user => this.shownTasks()[user.id] ?? [])
    );

    /** Tasks per user that match the search (all states). */
    private readonly searchedTasks = computed(() => {
        const result: { [userId: number]: AzureTask[] } = {};
        for (const user of this.users()) {
            result[user.id] = (this.userTasks()[user.id] ?? []).filter(task => this.taskMatchesSearch(task, user.id));
        }
        return result;
    });

    /** Tasks per user as listed: search + status filter. */
    readonly shownTasks = computed(() => {
        const filter = this.statusFilter();
        const result: { [userId: number]: AzureTask[] } = {};
        for (const [userId, tasks] of Object.entries(this.searchedTasks())) {
            result[+userId] = filter === 'alle' ? tasks : tasks.filter(task => task.state === filter);
        }
        return result;
    });

    /** Reordering is saved per user, so it only works on the complete, unfiltered list. */
    readonly taskSortingDisabled = computed(() => !!this.taskSearchTerm().trim() || this.statusFilter() !== 'alle');

    private readonly companyByAzureProject = computed(() => new Map(
        (this.platforms() ?? []).filter(platform => platform?.company?.azure_project_id)
            .map(platform => [platform.company.azure_project_id, platform.company_id])
    ));

    /** Display values per task id for app-task-row. */
    readonly taskViews = computed(() => {
        const companies = this.companyByAzureProject();
        const shortName = new ProjectShortNamePipe();
        const views = new Map<number, TaskView>();
        for (const tasks of Object.values(this.userTasks())) {
            for (const task of tasks) {
                const priority = this.taskPriority(task);
                views.set(task.id, {
                    href: this.workItemUrl(task.project, task.id),
                    parentHref: task.parent_id ? this.workItemUrl(task.project, task.parent_id) : null,
                    client: shortName.transform(task.project),
                    path: [
                        task.grandparent_id ? {label: task.grandparent_title, href: this.workItemUrl(task.project, task.grandparent_id)} : null,
                        task.parent_id ? {label: task.parent_title, href: this.workItemUrl(task.project, task.parent_id)} : null,
                    ].filter(part => !!part),
                    companyId: companies.get(task.project_id) ?? null,
                    priority,
                    priorityLabel: [PRIORITY_LABELS[priority], task.workItemType, task.severity].filter(part => !!part).join(' · '),
                    statusTone: taskStatusTone(task.state),
                });
            }
        }
        return views;
    });

    /** Urenbesteding and urenindicatie of the listed tasks per user, in hours. */
    readonly taskHours = computed(() => {
        const result: { [userId: number]: { spent: number; estimate: number } } = {};
        for (const [userId, tasks] of Object.entries(this.shownTasks())) {
            const counted = tasks.filter(task => countsForHours(task));
            result[+userId] = {
                spent: counted.reduce((sum, task) => sum + (task.hours_spent ?? 0), 0),
                estimate: counted.reduce((sum, task) => sum + (task.hours_estimate ?? 0), 0),
            };
        }
        return result;
    });

    /** Status chips with the counts of the current view (after search). */
    readonly statusOptions = computed<FilterOption[]>(() => {
        const view = this.view();
        const searched = this.searchedTasks();
        let pool: AzureTask[];
        if (view === 'kanban') {
            pool = this.kanbanPool().map(item => item.task).filter(task => KANBAN_STATES.includes(task.state));
        } else if (view === 'colleagues') {
            pool = this.users().filter(user => this.userHasResults(user.id)).flatMap(user => searched[user.id] ?? []);
        } else {
            pool = searched[this.activeUserId()] ?? [];
        }
        const states = view === 'kanban'
            ? KANBAN_STATES
            : [...DAILY_STATES, ...[...new Set(pool.map(task => task.state))].filter(state => state && !DAILY_STATES.includes(state))];
        return [
            {value: 'alle', label: 'Alle', count: pool.length, color: '#9aa0a6'},
            ...states.map(state => ({value: state, label: state, count: pool.filter(task => task.state === state).length, color: taskStatusDot(state)}))
        ];
    });

    /** All tasks of the selected employees (all when none is selected) that match the search. */
    readonly kanbanPool = computed(() => {
        const selected = this.kanbanUserIds();
        const searched = this.searchedTasks();
        return this.sortedUsers()
            .filter(user => !selected.length || selected.includes(user.id))
            .flatMap(user => (searched[user.id] ?? []).map(task => ({task, user, owner: {name: user.name, seed: user.id}})));
    });

    readonly kanbanColumns = computed(() => {
        const filter = this.statusFilter();
        const states = KANBAN_STATES.includes(filter) ? [filter] : KANBAN_STATES;
        const pool = this.kanbanPool();
        return states.map(state => ({state, dot: taskStatusDot(state), items: pool.filter(item => item.task.state === state)}));
    });

    readonly kanbanUserOptions = computed<FilterOption<number | 'all'>[]>(() => {
        const searched = this.searchedTasks();
        return [
            {value: 'all', label: 'Iedereen'},
            ...this.sortedUsers()
                .filter(user => this.userTasks()[user.id]?.length)
                .map(user => ({
                    value: user.id,
                    label: user.name.split(' ')[0],
                    count: (searched[user.id] ?? []).filter(task => KANBAN_STATES.includes(task.state)).length,
                    avatar: {name: user.name, seed: user.id}
                }))
        ];
    });

    readonly kanbanUserValues = computed<(number | 'all')[]>(() => this.kanbanUserIds().length ? this.kanbanUserIds() : ['all']);

    /** Open pull requests per user, grouped per Azure project, after the search. */
    readonly prGroups = computed(() => {
        const result: { [userId: number]: { project: string; error?: string; prs: PrView[] }[] } = {};
        const userByName = new Map(this.users().map(user => [user.name?.toLowerCase(), user.id]));
        for (const [userId, projects] of Object.entries(this.userPRs())) {
            result[+userId] = projects.map(project => ({
                project: project.project,
                error: project.error,
                prs: (project.prs ?? []).filter(pr => this.prMatchesSearch(pr, project.project)).map(pr => ({
                    pr,
                    project: project.project,
                    href: 'https://dev.azure.com/codalt/' + project.project + '/_git/' + pr.repository.name + '/pullrequest/' + pr.pullRequestId + '?_a=files',
                    when: this.formatWhen(pr.creationDate),
                    reviewers: (pr.reviewers ?? []).map(reviewer => ({
                        name: reviewer.displayName,
                        seed: userByName.get(reviewer.displayName?.toLowerCase()) ?? reviewer.displayName,
                        vote: voteFromAzure(reviewer.vote)
                    }))
                }))
            })).filter(group => group.prs.length || group.error);
        }
        return result;
    });

    /** Two weeks of registered and contract hours per user (current + previous/next week, see generateWeekList). */
    readonly hoursByUser = computed(() => {
        const hours = this.weeklyHours();
        const today = this.localDate(new Date());
        const result: { [userId: number]: HoursWeekView[] } = {};
        for (const user of this.users()) {
            result[user.id] = this.weekList().map(week => this.hoursWeek(user.id, week, hours, today));
        }
        return result;
    });

    readonly pageTitle = computed(() => {
        switch (this.view()) {
            case 'colleagues':
                return 'Alle collega\'s';
            case 'kanban':
                return 'Kanban';
        }
        const activeUserId = this.activeUserId();
        return activeUserId === this.LocalStorage.user?.id
            ? 'Mijn daily'
            : 'Daily van ' + (this.users().find(user => user.id === activeUserId)?.name ?? '');
    });

    readonly pageSubtitle = computed(() => {
        const today = this.today;
        const date = formatDate(today, 'EEE d MMM y', 'nl');
        switch (this.view()) {
            case 'colleagues':
                return `${date} · ${this.users().filter(user => this.userHasResults(user.id)).length} collega's`;
            case 'kanban': {
                const selected = this.kanbanUserIds().length;
                return `${date} · ${selected ? selected + ' van ' + (this.kanbanUserOptions().length - 1) + ' medewerkers' : 'alle medewerkers'}`;
            }
        }
        return date;
    });

    readonly absentTodayLabels = computed(() => {
        const today = new Date();
        const dateStr = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())).toISOString().slice(0, 10);
        const absentDates = this.userAbsentDates();
        const labels: { [userId: number]: { name: string; time: string } } = {};

        for (const userId of Object.keys(absentDates).map(Number)) {
            const absent = absentDates[userId].get(dateStr);
            if (!absent) {
                continue;
            }
            const wholeDay = absent.endMinutes - absent.startMinutes === 8 * 60;
            labels[userId] = {
                name: absent.externalName ?? 'Verlof',
                time: wholeDay ? '' : `${absent.begintime?.slice(0, 5)} - ${absent.endtime?.slice(0, 5)}`
            };
        }
        return labels;
    });

    readonly absentLookup = computed(() => {
        const absentDates = this.userAbsentDates();
        const lookup: { [userId: number]: { [week: string]: { [isoDay: number]: { absent: boolean; color: string | null; startMinutes: number; endMinutes: number; externalName?: string; begintime?: string; endtime?: string } } } } = {};
        for (const userId of Object.keys(absentDates).map(Number)) {
            lookup[userId] = {};
            for (const weekStr of this.weekList()) {
                const monday = this.weekStringToMonday(weekStr);
                lookup[userId][weekStr] = {};
                for (let isoDay = 1; isoDay <= 7; isoDay++) {
                    const date = new Date(monday);
                    date.setUTCDate(monday.getUTCDate() + (isoDay === 1 ? 6 : isoDay - 2));
                    const dateStr = date.toISOString().slice(0, 10);
                    const entryMap = absentDates[userId];
                    const entry = entryMap?.get(dateStr) ?? null;
                    const absent = entry !== null;
                    lookup[userId][weekStr][isoDay] = {
                        absent,
                        color: absent ? entry.color : null,
                        startMinutes: absent ? entry.startMinutes : 0,
                        endMinutes: absent ? entry.endMinutes : 0,
                        externalName: absent ? entry.externalName : undefined,
                        begintime: absent ? entry.begintime : undefined,
                        endtime: absent ? entry.endtime : undefined,
                    };
                }
            }
        }
        return lookup;
    });

    private static readonly reloadAfterMs = 2 * 60 * 1000;

    private contextMenuOverlayRef: OverlayRef | null = null;
    // Azure tasks/PRs, hours and leaves are fetched once (no websocket updates), so they go stale
    // while the tab sits in the background. Kept in their own bag so a reload cancels the previous run.
    private dataSubscriptions = new Subscription();
    private lastDataLoad = 0;

    constructor(
        private azureService: AzureService,
        private leaveService: LeaveService,
        private overlay: Overlay,
        private platformService: PlatformService,
        private registrationService: RegistrationService,
        private userService: UserService,
        private viewContainerRef: ViewContainerRef,
        private azureTaskSortingService: AzureTaskSortingService,
        private route: ActivatedRoute,
        private router: Router,
    ) {
        super();
    }

    today: Date = new Date();
    readonly weeklyHours = signal<any>({});

    @HostListener('document:click')
    closeContextMenu(): void {
        this.contextMenuOverlayRef?.dispose();
        this.contextMenuOverlayRef = null;
    }

    ngOnInit(): void {
        this.showChanged.set(localStorage.getItem('daily_show_changed') === 'true');
        this.lastSelectedTask.set(localStorage.getItem('last_selected_task') ? +localStorage.getItem('last_selected_task') : null);

        // The selected colleague is kept in the URL (?user=12), so a link or a reload opens their daily
        this.subscriptions.add(
            this.route.queryParamMap.subscribe(params => {
                const userId = +params.get('user') || null;
                if (userId !== this.selectedUserId()) {
                    this.selectedUserId.set(userId);
                }
            })
        );

        this.subscriptions.add(
            this.userService.getList().subscribe(users => {
                this.users.set(
                    users.filter(user => {
                        const start = user['employment_start'] ? new Date(user['employment_start']) : null;
                        const end = user['employment_end'] ? new Date(user['employment_end']) : null;
                        const startOk = !start || start <= this.today;
                        const endOk = !end || end >= this.today;
                        return startOk && endOk;
                    })
                );
                this.loadData();
            })
        );

        this.subscriptions.add(
            this.platformService.getPlatformsLive().subscribe({
                next: (platforms) => {
                    this.platforms.set(platforms.map(p => ({...p})));
                },
                error: err => console.error(err)
            })
        );

        this.subscriptions.add(
            this.azureTaskSortingService.getList().subscribe({
                next: sortOrders => this.applyTaskOrderUpdate(sortOrders),
                error: err => console.error(err)
            })
        );
    }

    ngOnDestroy() {
        this.dataSubscriptions.unsubscribe();
        super.ngOnDestroy();
    }

    // Reloads everything that isn't kept up to date over the websocket. The search term and the
    // other view state are signals that aren't touched here, so they survive the reload.
    private loadData(): void {
        this.dataSubscriptions.unsubscribe();
        this.dataSubscriptions = new Subscription();
        this.lastDataLoad = Date.now();

        this.generateWeekList();
        this.loadWeeklyHours();

        // Load PRs and Tasks in parallel, then attach PRs to tasks
        this.dataSubscriptions.add(
            forkJoin({
                prs: this.azureService.getOpenPullRequests(),
                tasks: this.azureService.getOpenTasks()
            }).subscribe(({prs, tasks}) => {
                this.openPRs.set(prs || []);

                // Map PRs per user
                this.mapPRsPerUser();

                const userTasks: { [userId: number]: AzureTask[] } = {};
                const emailToUserId: { [email: string]: number } = {};
                this.users().forEach(user => {
                    if (user.email) {
                        emailToUserId[user.email.toLowerCase()] = user.id;
                    }
                });
                (tasks || []).forEach((task: AzureTask) => {
                    const email = task.assignedTo?.email?.toLowerCase();
                    const userId = emailToUserId[email];
                    if (userId) {
                        if (!userTasks[userId]) userTasks[userId] = [];
                        userTasks[userId].push(task);
                    }
                });
                Object.keys(userTasks).forEach(userId => {
                    userTasks[+userId] = this.sortTasks(+userId, userTasks[+userId]);
                });

                // Both PRs and Tasks are loaded and the board can render right away —
                // PR work item links are fetched afterwards and merged in as they arrive.
                this.userTasks.set(userTasks);

                this.loadPrWorkItemLinks();
            })
        );

        this.loadQueryLinks();
    }

    @HostListener('document:visibilitychange')
    @HostListener('window:focus')
    reloadWhenTabReopened(): void {
        if (document.visibilityState !== 'visible' || !this.lastDataLoad) {
            return;
        }
        if (Date.now() - this.lastDataLoad < DailyComponent.reloadAfterMs) {
            return;
        }
        this.loadData();
    }

    private moveUserSelection(step: number, event: Event): void {
        if (this.view() !== 'mine' || (event.target as HTMLElement)?.closest('input, textarea, select')) {
            return;
        }
        const users = this.userSwitchList();
        if (!users.length) {
            return;
        }
        const current = users.findIndex(user => user.id === this.activeUserId());
        this.selectUser(users[(current + step + users.length) % users.length].id);
        event.preventDefault();
    }

    private markUserSeenAndMove(step: number, event: Event): void {
        if (this.view() !== 'mine' || (event.target as HTMLElement)?.closest('input, textarea, select')) {
            return;
        }
        const users = this.sortedUsers();
        if (!users.length) {
            return;
        }
        event.preventDefault();

        const current = users.findIndex(user => user.id === this.activeUserId());
        if (current > -1) {
            this.markUserSeen(users[current].id);
        }
        for (let offset = 1; offset <= users.length; offset++) {
            const next = users[(((current + step * offset) % users.length) + users.length) % users.length];
            if (!this.markedUserIds().includes(next.id)) {
                this.selectUser(next.id);
                return;
            }
        }
    }

    private markUserSeen(userId: number): void {
        this.seenUserIds.update(seenUserIds =>
            seenUserIds.includes(userId) ? seenUserIds : [...seenUserIds, userId]
        );
    }

    private clearSelectedTask(): void {
        this.lastSelectedTask.set(null);
        localStorage.removeItem('last_selected_task');
    }

    private moveTaskSelection(step: number, event: Event): void {
        const tasks = this.visibleTasks();
        if (!tasks.length) {
            return;
        }
        const current = tasks.findIndex(task => task.id === this.lastSelectedTask());
        const next = current === -1
            ? (step > 0 ? tasks[0] : tasks[tasks.length - 1])
            : tasks[(current + step + tasks.length) % tasks.length];

        this.selectTask(next, event);
    }

    private selectTask(task: AzureTask, event: Event): void {
        if (!task || (event.target as HTMLElement)?.closest('input, textarea, select')) {
            return;
        }
        this.updateLastClickedTask(task);
        this.scrollTaskIntoView(task.id);
        event.preventDefault();
    }

    private scrollTaskIntoView(taskId: number): void {
        setTimeout(() => document.querySelector(`[data-task-id="${taskId}"]`)?.scrollIntoView({block: 'nearest'}));
    }

    private applyTaskOrderUpdate(sortOrders: AzureTaskSorting[]): void {
        const orderByTaskId = new Map(sortOrders.map(sortOrder => [sortOrder.task_id, sortOrder.order]));

        const updatedUserTasks: { [userId: number]: AzureTask[] } = {};
        Object.keys(this.userTasks()).forEach(userId => {
            const tasks = this.userTasks()[+userId].map(task =>
                orderByTaskId.has(task.id) ? {...task, order: orderByTaskId.get(task.id)} : task
            );
            updatedUserTasks[+userId] = this.sortTasks(+userId, tasks);
        });
        this.userTasks.set(updatedUserTasks);
    }

    generateWeekList() {
        const today = new Date();
        const getISOWeekYear = (d: Date): [number, number] => {
            const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
            dt.setUTCDate(dt.getUTCDate() + 4 - (dt.getUTCDay() || 7));
            const year = dt.getUTCFullYear();
            const week = Math.ceil((((dt.getTime() - new Date(Date.UTC(year, 0, 1)).getTime()) / 86400000) + 1) / 7);
            return [year, week];
        };
        const [curYear, curWeek] = getISOWeekYear(today);
        const isoDay = today.getUTCDay() || 7; // 1=Mon...7=Sun
        const otherWeekDate = new Date(today);
        otherWeekDate.setDate(otherWeekDate.getDate() + (isoDay >= 4 ? 7 : -7));
        const [otherYear, otherWeek] = getISOWeekYear(otherWeekDate);
        if (isoDay >= 4) {
            this.weekList.set([`${curYear}-${curWeek}`, `${otherYear}-${otherWeek}`]);
        } else {
            this.weekList.set([`${otherYear}-${otherWeek}`, `${curYear}-${curWeek}`]);
        }
    }

    loadWeeklyHours() {
        this.dataSubscriptions.add(
            this.registrationService.getWeeklyHours().subscribe(res => {
                this.weeklyHours.set(res ?? {});
            })
        );
        const prevMonday = this.weekStringToMonday(this.weekList()[0]);
        const sunday = new Date(this.weekStringToMonday(this.weekList()[1]));
        sunday.setUTCDate(sunday.getUTCDate() + 6);

        this.dataSubscriptions.add(
            forkJoin({
                leaves: this.leaveService.getLeaves(true, prevMonday, sunday),
                leaveTypes: this.leaveService.getLeaveTypes()
            }).subscribe(({leaves, leaveTypes}) => {
                this.leaveTypes.set(leaveTypes);
                const leaveTypeColorMap = new Map(leaveTypes.map(lt => [lt.key, lt.color ?? null]));
                const externalNameMap = new Map(leaveTypes.map(lt => [lt.key, lt.external_name ?? lt.name]));

                const map: { [userId: number]: Map<string, { color: string | null; startMinutes: number; endMinutes: number; externalName?: string; begintime?: string; endtime?: string }> } = {};
                const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                for (const leave of leaves) {
                    if (!leave.begindate || !leave.enddate) continue;
                    const affectedUsers = leave.common ? this.users() : leave.users;
                    if (!affectedUsers?.length) continue;
                    const color = leaveTypeColorMap.get(leave.leave_type) ?? null;
                    const start = new Date(leave.begindate), end = new Date(leave.enddate);
                    const leaveStart = new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate()));
                    const leaveEnd = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()));
                    const intervalWeek = leave.interval_week ?? 0;

                    // Parse begintime/endtime (HH:mm:ss) to minutes from midnight
                    const beginParts = (leave.begintime || '00:00:00').split(':').map(Number);
                    const endParts = (leave.endtime || '23:59:00').split(':').map(Number);
                    const startMinutes = beginParts[0] * 60 + (beginParts[1] || 0);
                    const endMinutes = endParts[0] * 60 + (endParts[1] || 0);

                    // Loop from prevMonday to sunday, bounded by leave start/end
                    const loopFrom = new Date(Math.max(prevMonday.getTime(), leaveStart.getTime()));
                    const loopUntil = new Date(Math.min(sunday.getTime(), leaveEnd.getTime()));

                    for (const d = new Date(loopFrom); d <= loopUntil; d.setUTCDate(d.getUTCDate() + 1)) {
                        let generateThisDay = intervalWeek < 1;

                        if (intervalWeek > 0) {
                            const week = +formatDate(d, 'w', 'nl');
                            const firstWeek = +formatDate(leaveStart, 'w', 'nl');
                            const thisWeek = ((week - firstWeek) % intervalWeek) === 0;
                            const weekDayName = formatDate(d, 'EEEE', 'en').toLowerCase();
                            const thisDay = !!leave[`repeat_${weekDayName}`];
                            generateThisDay = thisWeek && thisDay;
                        }

                        if (generateThisDay) {
                            for (const user of affectedUsers) {
                                if (!map[user.id]) map[user.id] = new Map();
                                map[user.id].set(d.toISOString().slice(0, 10), {
                                    color,
                                    startMinutes,
                                    endMinutes,
                                    externalName: externalNameMap.get(leave.leave_type),
                                    begintime: leave.begintime,
                                    endtime: leave.endtime
                                });
                            }
                        }
                    }
                }
                this.userAbsentDates.set(map);
            })
        );
    }

    loadQueryLinks() {
        const iconMap: Record<string, string> = {
            'b650e79e-cd92-44de-8d23-f5423a7092c0': 'fa-list-check',
            '0d9d1c1e-f025-455d-8622-c335d4edccdf': 'fa-users-slash',
            'b1a2efe3-5ef9-4975-b301-9de6fbd34f46': 'fa-helmet-safety',
            '28e4f2c7-0b91-444f-ac16-47f13cd2fa19': 'fa-person-chalkboard',
            '1ed11d36-368b-4ee5-bd22-17da95d70b95': 'fa-bug',
            '917c1217-5533-4177-905e-68dcb0257996': 'fa-phone',
        };

        this.dataSubscriptions.add(
            this.azureService.getQueryLinks().subscribe({
                next: res => this.queryLinks.set(
                    res.map(filter => ({...filter, icon: iconMap[filter.id] ?? 'fa-question'}))
                ),
                error: () => {
                }
            })
        );
    }

    private mapPRsPerUser(): void {
        const userPRs: { [userId: number]: AzureProjectPR[] } = {};
        const emailToUserId: { [email: string]: number } = {};
        this.users().forEach(user => {
            if (user.email) {
                emailToUserId[user.email.toLowerCase()] = user.id;
            }
        });
        this.openPRs().forEach(projectPR => {
            const userProjectMap: { [userId: number]: AzureProjectPR } = {};
            projectPR.prs.forEach(pr => {
                const email = pr.createdBy?.uniqueName?.toLowerCase();
                const userId = emailToUserId[email];
                if (userId) {
                    if (!userProjectMap[userId]) {
                        userProjectMap[userId] = {project: projectPR.project, prs: []};
                    }
                    userProjectMap[userId].prs.push(pr);
                }
            });
            Object.keys(userProjectMap).forEach(userId => {
                if (!userPRs[+userId]) userPRs[+userId] = [];
                userPRs[+userId].push(userProjectMap[userId]);
            });
        });
        this.userPRs.set(userPRs);
    }

    // Work item links per PR are fetched one call per PR, after the board has already rendered
    // without them (there's no reliable "changed" signal to batch/cache this server-side).
    private loadPrWorkItemLinks(): void {
        const prRefs: { project: string; pr: AzurePullRequest }[] = [];
        this.openPRs().forEach(projectPR => {
            projectPR.prs.forEach(pr => prRefs.push({project: projectPR.project, pr}));
        });

        if (!prRefs.length) {
            return;
        }

        this.dataSubscriptions.add(
            from(prRefs).pipe(
                mergeMap(({project, pr}) =>
                    this.azureService.getPullRequestWorkItems(project, pr.repository.id, pr.pullRequestId).pipe(
                        map(workItems => ({pr, workItems})),
                        catchError(() => of(null))
                    ), 6)
            ).subscribe(result => {
                if (!result) {
                    return;
                }
                result.pr.workItems = result.workItems;

                this.openPRs.set([...this.openPRs()]);
                this.attachRelatedPRsToTasks(this.userTasks());
                this.userTasks.set({...this.userTasks()});
            })
        );
    }

    private attachRelatedPRsToTasks(userTasks: { [userId: number]: AzureTask[] }): void {
        Object.values(userTasks).forEach(tasks => {
            tasks.forEach(task => {
                const taskIds = [task.id, task.parent_id, task.grandparent_id].filter(id => !!id);
                const relatedPRs: { project: string; pr: AzurePullRequest }[] = [];
                this.openPRs().forEach(projectPR => {
                    projectPR.prs.forEach(pr => {
                        const hasRelatedWorkItem = pr.workItems?.some(wi => taskIds.includes(wi.id));
                        if (hasRelatedWorkItem) {
                            relatedPRs.push({project: projectPR.project, pr});
                        }
                    });
                });
                task.relatedPRs = relatedPRs;
            });
        });
    }


    openContextMenu(event: MouseEvent, task: AzureTask): void {
        event.preventDefault();
        event.stopPropagation();

        this.contextMenuOverlayRef?.dispose();
        this.contextMenuOverlayRef = null;

        const companyPlatforms = this.platforms().filter(platform => platform?.company?.azure_project_id === task.project_id);
        const filterPlatforms = companyPlatforms.filter(platform => {
            const findResult = platform.azure_features?.find(af => {
                return af.azure_feature_id === task.grandparent_id || af.azure_feature_id === task.parent_id;
            });
            return findResult !== undefined;
        });

        this.contextMenuTask.set({
            azure_task: task,
            platform: filterPlatforms?.length ? filterPlatforms : companyPlatforms,
            relatedPRs: task.relatedPRs ?? []
        });

        this.contextMenuOverlayRef = this.overlay.create({
            positionStrategy: this.getContextMenuPositionStrategy(event),
            scrollStrategy: this.overlay.scrollStrategies.close(),
        });

        this.contextMenuOverlayRef.attach(new TemplatePortal(this.taskContextMenuTpl, this.viewContainerRef));
    }

    private getContextMenuPositionStrategy(event: MouseEvent): FlexibleConnectedPositionStrategy {
        const fallbackPositions: ConnectedPosition[] = [
            {originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top'},
            {originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom'},
            {originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top'},
            {originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'bottom'},
        ];

        return this.overlay.position()
            .flexibleConnectedTo({x: event.clientX, y: event.clientY})
            .withPositions(fallbackPositions)
            .withViewportMargin(8)
            .withPush(true);
    }

    openContextMenuForPR(event: MouseEvent, pr: AzurePullRequest, projectName: string): void {
        event.preventDefault();
        event.stopPropagation();

        this.contextMenuOverlayRef?.dispose();
        this.contextMenuOverlayRef = null;

        // Create a synthetic task object to display PR in context menu
        const syntheticTask: AzureTask = {
            id: pr.pullRequestId,
            title: pr.title,
            parent_title: null,
            parent_id: null,
            parent_workItemType: null,
            grandparent_title: null,
            grandparent_id: null,
            grandparent_workItemType: null,
            state: pr.status,
            project: projectName,
            project_id: null,
            workItemType: 'Pull Request',
            severity: null,
            assignedTo: null,
            changed_by: pr.createdBy?.displayName,
            changed_date: pr.creationDate,
            url: pr.url,
            order: null,
            relatedPRs: [{project: projectName, pr}]
        };

        this.contextMenuTask.set({
            azure_task: syntheticTask,
            platform: null,
            relatedPRs: [{project: projectName, pr}]
        });

        this.contextMenuOverlayRef = this.overlay.create({
            positionStrategy: this.getContextMenuPositionStrategy(event),
            scrollStrategy: this.overlay.scrollStrategies.close(),
        });

        this.contextMenuOverlayRef.attach(new TemplatePortal(this.taskContextMenuTpl, this.viewContainerRef));
    }

    onTaskSearch(value: string): void {
        // this uses regex, the regex is to see if the provided value is numeric, if any part of the value is not numeric it will be false
        if (/^\d+$/.test(value.trim())) {
            window.open(`https://dev.azure.com/codalt/_workitems/edit/${value.trim()}`, '_blank');
            return;
        }
        const firstTask = this.getFirstMatchingTask();
        if (firstTask) {
            window.open(`https://dev.azure.com/codalt/${firstTask.project}/_workitems/edit/${firstTask.id}`, '_blank');
        } else if(!this.showColleagues() && !!this.getFirstMatchingTask(true))
        {
            this.toggleColleagues(true);
        }
        else {
            window.open(`https://dev.azure.com/codalt/_search?text=${value}&type=workitem&pageSize=25`, '_blank');
        }
    }

    getFirstMatchingTask(includeAll = false): AzureTask | null {
        if (!this.taskSearchTerm().trim()) {
            return null;
        }
        for (const user of this.users()) {
            if(includeAll || !this.activeUserId() || this.activeUserId() === user.id) {
                const match = this.userTasks()[user.id]?.find(task => this.taskMatchesSearch(task, user.id));
                if (match) return match;
            }
        }
        return null;
    }

    userNameMatchesSearch(userId: number): boolean {
        const term = this.taskSearchTerm().trim().toLowerCase();
        if (!term) {
            return false;
        }
        const user = this.users().find(u => u.id === userId);
        return !!user?.name?.toLowerCase().includes(term);
    }

    taskMatchesSearch(task: AzureTask, userId?: number): boolean {
        const term = this.taskSearchTerm().trim().toLowerCase();
        if (!term) {
            return true;
        }

        // A matching employee name shows all of their tasks
        if (userId !== undefined && this.userNameMatchesSearch(userId)) {
            return true;
        }

        // Check task properties
        const taskMatches = task.title?.toLowerCase().includes(term) ||
            task.parent_title?.toLowerCase().includes(term) ||
            task.grandparent_title?.toLowerCase().includes(term) ||
            task.project?.toLowerCase().includes(term) ||
            String(task.id).includes(term) ||
            String(task.parent_id).includes(term) ||
            String(task.grandparent_id).includes(term);

        if (taskMatches) return true;

        // Check related PRs
        if (task.relatedPRs?.length) {
            return task.relatedPRs.some(item =>
                String(item.pr.pullRequestId).includes(term) ||
                item.pr.title?.toLowerCase().includes(term) ||
                item.project?.toLowerCase().includes(term)
            );
        }

        return false;
    }

    prMatchesSearch(pr: any, projectName: string): boolean {
        const term = this.taskSearchTerm().trim().toLowerCase();
        if (!term) {
            return true;
        }
        return pr.title?.toLowerCase().includes(term) ||
            String(pr.pullRequestId).includes(term) ||
            projectName?.toLowerCase().includes(term);
    }

    userHasResults(userId: number): boolean {
        const term = this.taskSearchTerm().trim().toLowerCase();
        if (!term) {
            return true;
        }
        if (this.userNameMatchesSearch(userId)) {
            return true;
        }
        const hasMatchingTask = this.userTasks()[userId]?.some(task => this.taskMatchesSearch(task, userId));
        const hasMatchingPR = this.userPRs()[userId]?.some(projectPR =>
            projectPR.prs?.some(pr => this.prMatchesSearch(pr, projectPR.project))
        );
        return hasMatchingTask || hasMatchingPR;
    }


    @HostListener('document:keydown.arrowleft', ['$event'])
    selectPreviousUser(event: Event) {
        this.moveUserSelection(-1, event);
    }

    @HostListener('document:keydown.arrowright', ['$event'])
    selectNextUser(event: Event) {
        this.moveUserSelection(1, event);
    }

    @HostListener('document:keydown.control.arrowleft', ['$event'])
    markAndSelectPreviousUser(event: Event) {
        this.markUserSeenAndMove(-1, event);
    }

    @HostListener('document:keydown.control.arrowright', ['$event'])
    markAndSelectNextUser(event: Event) {
        this.markUserSeenAndMove(1, event);
    }

    @HostListener('document:keydown.arrowup', ['$event'])
    selectPreviousTask(event: Event) {
        this.moveTaskSelection(-1, event);
    }

    @HostListener('document:keydown.arrowdown', ['$event'])
    selectNextTask(event: Event) {
        this.moveTaskSelection(1, event);
    }

    @HostListener('document:keydown.pageup', ['$event'])
    selectFirstTask(event: Event) {
        this.selectTask(this.visibleTasks()[0], event);
    }

    @HostListener('document:keydown.pagedown', ['$event'])
    selectLastTask(event: Event) {
        this.selectTask(this.visibleTasks()[this.visibleTasks().length - 1], event);
    }

    @HostListener('document:keydown.enter', ['$event'])
    openSelectedTask(event: Event) {
        if ((event.target as HTMLElement)?.closest('input, textarea, select')) {
            return;
        }
        const task = this.visibleTasks().find(task => task.id === this.lastSelectedTask());
        if (!task) {
            return;
        }
        window.open(`https://dev.azure.com/codalt/${task.project}/_workitems/edit/${task.id}`, '_blank');
        event.preventDefault();
    }

    @HostListener('document:keydown.escape')
    clearSearchOrSeenUsers() {
        if (this.taskSearchTerm()) {
            this.taskSearchTerm.set('');
            this.taskSearch.nativeElement.value = '';
            return;
        }
        if(this.seenUserIds().length !== 0) {
            this.seenUserIds.set([]);
            return;
        }
        this.toggleColleagues(false);
    }

    selectUser(userId: number) {
        this.selectedUserId.set(userId);
        this.clearSelectedTask();
        this.setUserParam(userId);
    }

    /** Your own daily has no ?user; replaceUrl, so switching colleagues doesn't fill the browser history. */
    private setUserParam(userId: number | null) {
        const user = userId && userId !== this.LocalStorage.user?.id ? userId : null;
        this.router.navigate([], {relativeTo: this.route, queryParams: {user}, queryParamsHandling: 'merge', replaceUrl: true});
    }

    toggleUserSeen(event: Event, userId: number) {
        event.preventDefault();
        this.seenUserIds.update(seenUserIds =>
            seenUserIds.includes(userId) ? seenUserIds.filter(id => id !== userId) : [...seenUserIds, userId]
        );
    }

    toggleColleagues(showColleagues: boolean) {
        if (showColleagues) {
            this.view.set('colleagues');
            this.selectedUserId.set(null);
            this.setUserParam(null);
        } else if (this.view() === 'colleagues') {
            this.view.set('mine');
        }
    }

    setView(view: DailyView) {
        if (view === 'colleagues') {
            this.toggleColleagues(true);
            return;
        }
        // Backlog and On hold have no kanban column
        if (view === 'kanban' && !KANBAN_STATES.includes(this.statusFilter())) {
            this.statusFilter.set('alle');
        }
        this.view.set(view);
    }

    taskView(task: AzureTask): TaskView {
        return this.taskViews().get(task.id);
    }

    /** " · besteed 12:30 · indicatie 40:00" after the task count, when any listed task has hours. */
    hoursMeta(userId: number): string {
        const hours = this.taskHours()[userId];
        if (!hours?.spent && !hours?.estimate) {
            return '';
        }
        const format = (value: number) => formatDuration(Math.round(value * 60));
        return ` · besteed ${format(hours.spent)} · indicatie ${format(hours.estimate)}`;
    }

    prCountLabel(userId: number): string {
        const count = (this.prGroups()[userId] ?? []).reduce((sum, group) => sum + group.prs.length, 0);
        return count ? `${count} open` : 'Geen open';
    }

    openRegistration(day: HoursDay) {
        if (day.date) {
            this.router.navigate(['/', this.Routenames.registration], {queryParams: {date: day.date}});
        }
    }

    /** "Collega's": clicking a name opens the daily of that person. */
    openColleague(userId: number) {
        this.view.set('mine');
        this.selectUser(userId);
    }

    setKanbanUsers(values: (number | 'all')[]) {
        const wasAll = !this.kanbanUserIds().length;
        const userIds = values.filter(value => value !== 'all') as number[];
        this.kanbanUserIds.set(values.includes('all') && !wasAll ? [] : userIds);
    }

    /** M: marks (or unmarks) the person whose daily is open. */
    @HostListener('document:keydown.m', ['$event'])
    toggleActiveUserSeen(event: Event) {
        if (this.view() !== 'mine' || (event.target as HTMLElement)?.closest('input, textarea, select')) {
            return;
        }
        this.toggleUserSeen(event, this.activeUserId());
    }

    private taskPriority(task: AzureTask): TaskPriority {
        // README open point 3: the stripes show the priority (assumed).
        // TODO(design): Azure only gives us a severity for bugs (no priority field is fetched), so every
        //  other work item shows "normaal". A critical bug also gets a red row, like the old daily.
        if (this.isCriticalBug(task)) {
            return 'critical';
        }
        if (this.isHighPriorityBug(task)) {
            return 'high';
        }
        return task.severity === '4 - Low' ? 'low' : 'normal';
    }

    private workItemUrl(project: string, id: number): string {
        return `https://dev.azure.com/codalt/${project}/_workitems/edit/${id}`;
    }

    private hoursWeek(userId: number, week: string, hours: any, today: string): HoursWeekView {
        const monday = this.weekStringToMonday(week);
        const dates: Date[] = [];
        const days: HoursDay[] = [2, 3, 4, 5, 6, 7, 1].map(isoDay => {
            const date = new Date(monday.getTime() + (isoDay === 1 ? 6 : isoDay - 2) * 24 * 60 * 60 * 1000);
            dates.push(date);
            const dateStr = date.toISOString().slice(0, 10);
            const absent = this.absentInfo(userId, week, isoDay);
            const decimal = hours?.[userId]?.[week]?.[isoDay];
            return {
                label: formatDate(date, 'EEE d', 'nl', 'UTC'),
                date: dateStr,
                minutes: decimal ? Math.round(decimal * 60) : null,
                today: dateStr === today,
                future: dateStr > today,
                weekend: isoDay === 1 || isoDay === 7,
                absent: absent.absent,
                background: absent.color
                    ? new AbsentGradientPipe().transform(absent.color, absent.startMinutes, absent.endMinutes)
                    : (absent.absent ? 'rgba(255, 160, 0, .18)' : null),
                tooltip: this.formatAbsentTooltip(absent),
            };
        });
        const first = dates[0], last = dates[dates.length - 1];
        const range = first.getUTCMonth() === last.getUTCMonth()
            ? `${formatDate(first, 'd', 'nl', 'UTC')}–${formatDate(last, 'd MMM', 'nl', 'UTC')}`
            : `${formatDate(first, 'd MMM', 'nl', 'UTC')} – ${formatDate(last, 'd MMM', 'nl', 'UTC')}`;
        const total = hours?.[userId]?.[week]?.['total'];
        const target = hours?.[userId]?.[week]?.['target'];
        return {
            title: `Week ${week.split('-')[1]}`,
            range,
            totalMinutes: total ? Math.round(total * 60) : 0,
            targetMinutes: target ? Math.round(target * 60) : 0,
            days
        };
    }

    private formatWhen(value: string): string {
        const date = new Date(value);
        if (isNaN(date.getTime())) {
            return '';
        }
        return formatDate(date, 'EEE d MMM, HH:mm', 'nl');
    }

    private localDate(date: Date): string {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }

    saveChangedDisplay(changedState: boolean) {
        this.showChanged.set(changedState);
        localStorage.setItem('daily_show_changed', changedState.toString());
    }

    updateLastClickedTask(task: AzureTask) {
        this.lastSelectedTask.set(task.id);
        localStorage.setItem('last_selected_task', String(task.id));
    }

    isHighPriorityBug(task: AzureTask): boolean {
        const isBug = task.workItemType?.toLowerCase() === 'bug';
        const isHighSeverity = task.severity === '2 - High' || task.severity === '1 - Critical';
        return isBug && isHighSeverity;
    }

    isCriticalBug(task: AzureTask): boolean {
        return task.workItemType?.toLowerCase() === 'bug' && task.severity === '1 - Critical';
    }

    isDraggableTask(task: AzureTask): boolean {
        return !this.isHighPriorityBug(task);
    }

    dropTask(event: CdkDragDrop<AzureTask[]>, userId: number) {
        const tasks = [...this.userTasks()[userId]];
        moveItemInArray(tasks, event.previousIndex, event.currentIndex);
        this.userTasks.update(current => ({...current, [userId]: tasks}));

        this.subscriptions.add(
            this.azureService.sortTasks(tasks.map(task => task.id)).subscribe({
                next: () => console.log('Task order saved successfully'),
                error: err => console.error('Error saving task order:', err)
            })
        );
    }

    private readonly defaultStatePriority = 2; // new, active, resolved, te testen, ...

    private defaultTaskPriority = (task: AzureTask): number => {
        if (this.isCriticalBug(task)) return 0;
        if (this.isHighPriorityBug(task)) return 1;
        const state = task.state?.toLowerCase();
        if (state === 'backlog') return 10;
        if (state === 'on hold') return 11;
        return this.defaultStatePriority;
    };

    private testerTaskPriority = (task: AzureTask): number => {
        const priority = this.defaultTaskPriority(task);
        if (priority !== this.defaultStatePriority) return priority; // bug, backlog, on hold — same as everyone else

        const state = task.state?.toLowerCase();
        if (state === 'active') return 2;
        if (state === 'te testen') return 3;
        if (state === 'new') return 4;
        if (state === 'resolved') return 5;
        return this.defaultStatePriority;
    };

    private testerTaskTiebreak = (a: AzureTask, b: AzureTask): number =>
        a.project.localeCompare(b.project)
        || new Date(b.changed_date).getTime() - new Date(a.changed_date).getTime();

    private defaultTaskTiebreak = (a: AzureTask, b: AzureTask): number => {
        if (a.order === null && b.order === null) return a.id - b.id;
        if (a.order === null) return 1;
        if (b.order === null) return -1;
        return a.order - b.order;
    };

    private sortTasks(userId: number, tasks: AzureTask[]): AzureTask[] {
        const user = this.users().find(u => u.id === userId);
        const isTester = AuthorisationService.hasRole(Roles.Tester, user);

        const priority = isTester ? this.testerTaskPriority : this.defaultTaskPriority;
        const tiebreak = isTester ? this.testerTaskTiebreak : this.defaultTaskTiebreak;

        return [...tasks].sort((a, b) => priority(a) - priority(b) || tiebreak(a, b));
    }

    absentInfo(userId: number, week: string, isoDay: number): { absent: boolean; color: string | null; startMinutes: number; endMinutes: number; externalName?: string; begintime?: string; endtime?: string } {
        return this.absentLookup()[userId]?.[week]?.[isoDay] ?? {absent: false, color: null, startMinutes: 0, endMinutes: 0};
    }

    formatAbsentTooltip(absent: any): string {
        if (!absent.absent) return '';
        const parts: string[] = [];
        if (absent.externalName) parts.push(absent.externalName);
        if (absent.begintime && absent.endtime) {
            parts.push(`${absent.begintime.slice(0, 5)} - ${absent.endtime.slice(0, 5)}`);
        }
        return parts.join('\n');
    }

    weekStringToMonday(weekStr: string): Date {
        const [year, week] = weekStr.split('-').map(Number);
        const jan4 = new Date(Date.UTC(year, 0, 4)); // Jan 4 is always in week 1
        const dayOfWeek = jan4.getUTCDay() || 7;
        const monday = new Date(jan4);
        monday.setUTCDate(jan4.getUTCDate() - (dayOfWeek - 1) + (week - 1) * 7);
        return monday;
    }

    @HostListener('window:keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        if (event.ctrlKey === true && event.key === "f") {
            event.preventDefault();
            this.taskSearch.nativeElement.focus();
        }

        if (event.key == 'Escape') {
            this.taskSearchTerm.set('');
            this.taskSearch.nativeElement.value = '';
        }
    }

    protected readonly Date = Date;
}
